import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const TABS = ["Neutral", "Professionell", "Behördlich"] as const;

const EXAMPLES: Record<string, { input: string; output: string; tags: string[] }> = {
  Neutral: {
    input: "ich möchte ihnen mitteilen das ich ab dem 15. mein urlaub nehme. können sie mir bescheid geben ob das ok ist",
    output: "Ich möchte Ihnen mitteilen, dass ich ab dem 15. meinen Urlaub nehme. Können Sie mir Bescheid geben, ob das in Ordnung ist?",
    tags: ["Rechtschreibung", "Zeichensetzung", "Tonalität", "Formelle Anrede"],
  },
  Professionell: {
    input: "der mitarbeiter hat seine arbeit nicht so gut gemacht wie erwartet wurde und wir müssen das ansprechen",
    output: "Der Mitarbeiter hat seine Arbeit nicht den Erwartungen entsprechend ausgeführt, und wir müssen dieses Thema ansprechen.",
    tags: ["Rechtschreibung", "Zeichensetzung", "Tonalität", "Formelle Anrede"],
  },
  Behördlich: {
    input: "hiermit kündige ich meinen vertrag weil ich unzufrieden bin mit dem service und will mein geld zurück",
    output: "Hiermit kündige ich meinen Vertrag. Der Grund hierfür ist meine Unzufriedenheit mit dem Service. Ich bitte um Rückerstattung des gezahlten Betrags.",
    tags: ["Rechtschreibung", "Zeichensetzung", "Tonalität", "Formelle Anrede"],
  },
};

export function EnterpriseDemoSection() {
  const [activeTab, setActiveTab] = useState<string>("Neutral");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ output: string; tags: string[] } | null>(null);
  const { ref, isVisible } = useScrollReveal({ threshold: 0.05 });

  const example = EXAMPLES[activeTab];

  const handleAnalyse = async () => {
    setIsLoading(true);
    setResult(null);
    // Simulate analysis
    await new Promise((r) => setTimeout(r, 900));
    setResult({ output: example.output, tags: example.tags });
    setIsLoading(false);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setResult(null);
  };

  return (
    <section id="demo" className="py-10 sm:py-16" aria-labelledby="demo-heading">
      <div className="container px-4">
        <div ref={ref} className={`mx-auto max-w-3xl reveal-base ${isVisible ? "revealed" : ""}`}>
          <h2 id="demo-heading" className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl text-center mb-10">
            Probieren Sie es selbst aus
          </h2>

          <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-border">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                    activeTab === tab
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="p-6">
              <Textarea
                value={example.input}
                readOnly
                className="min-h-[100px] resize-none bg-background border-border rounded-[var(--radius)] text-sm"
              />

              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleAnalyse}
                  disabled={isLoading}
                  size="sm"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird analysiert…
                    </>
                  ) : (
                    "Text analysieren"
                  )}
                </Button>
              </div>
            </div>

            {/* Result */}
            {result && (
              <div className="border-t border-border p-6 bg-secondary animate-fade-in">
                <p className="text-sm text-foreground leading-relaxed mb-4">
                  {result.output}
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="bg-success/10 text-success border-0 text-xs"
                    >
                      <Check className="mr-1 h-3 w-3" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
